//@flow
import * as React from 'react';
import { Stack, Column, Columns, Text, Box } from '@locus-taxy/neo-ui';
import { dashboardColors } from 'common/enums/ColorEnum';
import WorkingDaysSummary from 'common/entity/components/dayShift/WorkingDaysSummary';
import Button, { ButtonTypes } from 'common/components/core/button';
import HolidaySelector from 'common/entity/generic/HolidaySelector';
import Icon from 'common/components/core/icon';
import type { PjPlanConfigurationModelType } from 'common/models/flow/PjPlanConfiguration';
import type { TFunction } from 'react-i18next';

type DummyPersonaCardPropTypes = {
    index: number,
    deleteDummyPersona: (index: number) => void,
    editDummyPersona: (index: number) => void,
    openDummyPersonaModal: (index: number) => void,
    theme: Object,
    t: TFunction,
    salesmanProperties: number,
    salesCategories: List<string>,
    capacity: number
};

type DummyPersonaContainerPropTypes = {
    theme: Object,
    pjpConfiguration: PjPlanConfigurationModelType,
    deleteDummyPersona: (index: number) => void,
    editDummyPersona: (index: number) => void,
    openDummyPersonaModal: (index: number) => void,
    onAddDummyPersona: () => void,
    closeDummyPersonaModal: () => void,
    t: TFunction
};

class DummyPersonaCard extends React.PureComponent<DummyPersonaCardPropTypes> {
    deleteDummyPersona = (): void => {
        const { deleteDummyPersona, index } = this.props;
        deleteDummyPersona(index);
    };

    editDummypersona = (): void => {
        const { editDummyPersona, index } = this.props;
        editDummyPersona(index);
    };

    render() {
        const {
            theme,
            t,
            salesCategories,
            index,
            capacity,
            salesmanProperties,
            disabled
        } = this.props;
        if (salesCategories.size) {
            return (
                <Box className={theme.dummyPersonaProperties}>
                    <Stack space="small">
                        <Columns className={theme.dummyPersonaInfo}>
                            <Column width="fluid">
                                <Text fontSize={2} fontWeight="600">
                                    {t('pjpCreate.pjpConfig.persona')}{' '}
                                    {index + 1}
                                </Text>
                            </Column>
                            <Column
                                width="content"
                                className={theme.dummyPersonaActions}
                            >
                                <Icon
                                    theme={theme}
                                    fontSize={1}
                                    icon="edit-icon"
                                    onClick={
                                        disabled
                                            ? undefined
                                            : this.editDummypersona
                                    }
                                />

                                <Icon
                                    theme={theme}
                                    fontSize={1}
                                    icon="delete-bin"
                                    onClick={
                                        disabled
                                            ? undefined
                                            : this.deleteDummyPersona
                                    }
                                />
                            </Column>
                        </Columns>
                        <Columns>
                            <Column width="fluid">
                                <Text fontSize={1}>
                                    {t('pjpCreate.pjpConfig.category')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <Text fontSize={1}>
                                    {salesCategories.join(', ')}
                                </Text>
                            </Column>
                        </Columns>
                        <Columns>
                            <Column width="fluid">
                                <Text fontSize={1}>
                                    {t('pjpCreate.pjpConfig.capacity')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <Text fontSize={1}>{capacity}</Text>
                            </Column>
                        </Columns>
                        <Stack space="small">
                            <Column width="fluid">
                                <Text fontSize={1} fontWeight="600">
                                    {t('pjpCreate.pjpConfig.workingDays')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <WorkingDaysSummary
                                    slots={
                                        salesmanProperties.workingDays || null
                                    }
                                />
                            </Column>
                        </Stack>
                        <Stack space="small">
                            <Column width="fluid">
                                <Text fontSize={1}>
                                    {t('pjpCreate.pjpConfig.holidays')}
                                </Text>
                            </Column>
                            <Column width="content">
                                <HolidaySelector
                                    t={t}
                                    disableClick
                                    holidays={salesmanProperties.holidays}
                                />
                            </Column>
                        </Stack>
                        <Columns>
                            <Column width="fluid">
                                <Text fontSize={1}>
                                    {t(
                                        'pjpCreate.pjpConfig.startAtFirstLocation'
                                    )}
                                </Text>
                            </Column>
                            <Column width="content">
                                {salesmanProperties.shiftMetadata
                                    .startFromFirstCustomerVisit
                                    ? t('pjpCreate.pjpConfig.yes')
                                    : t('pjpCreate.pjpConfig.no')}
                            </Column>
                        </Columns>
                        <Columns>
                            <Column width="fluid">
                                <Text fontSize={1}>
                                    {t('pjpCreate.pjpConfig.endAtLastLocation')}
                                </Text>
                            </Column>
                            <Column width="content">
                                {salesmanProperties.shiftMetadata
                                    .endAtLastCustomerVisit
                                    ? t('pjpCreate.pjpConfig.yes')
                                    : t('pjpCreate.pjpConfig.no')}
                            </Column>
                        </Columns>
                    </Stack>
                </Box>
            );
        }
        return null;
    }
}

export default class DummyPersonaContainer extends React.PureComponent<DummyPersonaContainerPropTypes> {
    addDummyPersona = () => {
        const { onAddDummyPersona } = this.props;
        onAddDummyPersona();
    };
    render() {
        const {
            pjpConfiguration: {
                dummySalesmanProperties,
                salesmanCategoryGroups,
                salesmanCategoryGroupCapacities
            },
            openDummyPersonaModal,
            theme,
            editDummyPersona,
            deleteDummyPersona,
            t,
            disabled
        } = this.props;
        return (
            <Box className={theme.dummyPersonaView}>
                <Stack>
                    <Column width="1/4" sclassName={theme.addDummyButton}>
                        <Button
                            disabled={disabled}
                            buttonType={ButtonTypes.Text}
                            color={dashboardColors.darkSkyBlue.name}
                            label={t('pjpCreate.pjpConfig.addNewPersona')}
                            onClick={this.addDummyPersona}
                            uppercase
                            semibold
                            theme={theme}
                        />
                    </Column>

                    {salesmanCategoryGroups?.size
                        ? salesmanCategoryGroups
                              .map((salesmanCategoryGroup, key) => {
                                  return (
                                      <DummyPersonaCard
                                          disabled={disabled}
                                          t={t}
                                          deleteDummyPersona={
                                              deleteDummyPersona
                                          }
                                          editDummyPersona={editDummyPersona}
                                          salesCategories={
                                              salesmanCategoryGroup.categories
                                          }
                                          capacity={salesmanCategoryGroupCapacities.get(
                                              key
                                          )}
                                          openDummyPersonaModal={
                                              openDummyPersonaModal
                                          }
                                          index={key}
                                          key={key}
                                          theme={theme}
                                          salesmanProperties={dummySalesmanProperties.get(
                                              key
                                          )}
                                      />
                                  );
                              })
                              .toArray()
                        : null}
                </Stack>
            </Box>
        );
    }
}
